// The venue's printers, at the top of the Printers and hardware settings group (F-OPS-08 Printer management,
// F-OPS-09 cash drawer, F-STN-13 desk mode; spec/13 §11, spec/10 §10).
//
//  - Every printer with what it is (a desk printer a Clavian Counter PC drives, a cloud printer that polls, or
//    browser printing), what it prints (the purpose routing), its roll, and how it is doing — online, offline or
//    not heard from yet, with its last error in words.
//  - Adding or editing one is a short Stepper in a centred dialog (Type → Connection → Routing), not a wall of fields. A
//    desk printer names the Counter PC that drives it — only stations paired with the counter role are offered —
//    and may have the cash drawer wired to it. A cloud printer's polling URL is shown once, when it is added or
//    its key is rotated, with a copy button; the platform keeps only a hash of its key.
//  - Test print sends a test page straight to a printer (a browser printer prints it here), and the recent print
//    jobs say where each went and how it ended, so a failure is visible rather than silent.
//
// Everything here needs `settings:hardware`; without it the group says so, and the server refuses anyway.

import type { CreatePrinterRequest, Printer, PrinterKind, PrinterPurpose, PrintJob, Station, UpdatePrinterRequest } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Checkbox, Dialog, ListCard, ListRow, RadioGroup, SelectField, Skeleton, Stepper, Switch, TextField, useToast, type BadgeTone } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { useAccess } from '../api/queries';
import { PrintOutcomeNote, usePrintJobHandler, type PrintOutcome } from '../org/printing/outcome';
import {
  SETTINGS_HARDWARE,
  useArchivePrinter,
  useCreatePrinter,
  usePrinters,
  usePrintJobs,
  useTestPrinter,
  useUpdatePrinter,
} from '../org/printing/queries';
import { LoadFailure } from '../shell/LoadFailure';
import { Icon } from '../shell/nav-icons';
import { useCurrentVenue } from '../shell/scope';
import { InstallerDownloads } from '../stations/InstallerDownloads';
import { useStations } from '../stations/queries';
import { Notice, Section, TableSkeleton } from '../ui';

import './printers.css';

const KINDS: readonly PrinterKind[] = ['desk', 'cloud', 'browser'];
const PURPOSES: readonly PrinterPurpose[] = ['receipt', 'kitchen', 'bar', 'day_close'];
const WIDTHS = [80, 58] as const;
const PROTOCOLS = ['star_cloudprnt', 'epson_sdp'] as const;
const PINS = [2, 5] as const;
const DEFAULT_PORT = 9100;

const STATUS_TONE: Readonly<Record<Printer['status'], BadgeTone>> = { online: 'ok', offline: 'danger', unknown: 'neutral' };
const JOB_TONE: Readonly<Record<PrintJob['status'], BadgeTone>> = { queued: 'info', sent: 'info', printed: 'ok', failed: 'danger', expired: 'warn' };

/** A cloud printer's polling URL, shown once (on add, or a key rotation). */
interface RevealedUrl {
  readonly printerName: string;
  readonly url: string;
}

/** A station's name for the lists: its name, or "PC {number}". */
function stationName(station: Pick<Station, 'name' | 'number'>, t: ReturnType<typeof useTranslation>['t']): string {
  return station.name ?? t('printers.pc', { number: station.number });
}

export function PrintersGroup({ organizationId, venueId }: { readonly organizationId: string; readonly venueId: string }): ReactNode {
  const { t } = useTranslation('console');
  const access = useAccess({ venueId });
  const canManage = access.status === 'ready' && access.can(SETTINGS_HARDWARE);
  const printers = usePrinters(organizationId, venueId, canManage);
  const jobs = usePrintJobs(organizationId, venueId, canManage);
  const stations = useStations(organizationId, venueId);
  const [editor, setEditor] = useState<{ readonly mode: 'add' } | { readonly mode: 'edit'; readonly printer: Printer } | null>(null);
  const [revealed, setRevealed] = useState<RevealedUrl | null>(null);

  const stationRows = useMemo(() => (stations.data?.pages ?? []).flatMap((page) => page.data), [stations.data]);

  if (access.status !== 'ready') {
    return (
      <Section title={t('printers.title')} description={t('printers.hint')}>
        <Skeleton shape="text" lines={3} />
      </Section>
    );
  }
  if (!canManage) {
    return (
      <Section title={t('printers.title')} description={t('printers.hint')}>
        <Notice role="note">{t('printers.notPermitted')}</Notice>
      </Section>
    );
  }

  const list = printers.data ?? [];
  const onReveal = (printer: Printer) => {
    if (printer.cloud?.pollUrl != null) setRevealed({ printerName: printer.name, url: printer.cloud.pollUrl });
  };

  return (
    <>
      {revealed !== null ? <PollUrl revealed={revealed} onDismiss={() => setRevealed(null)} /> : null}
      {printers.data === undefined ? (
        <Section title={t('printers.title')} description={t('printers.hint')} body="rows">
          {printers.isError ? (
            <LoadFailure error={printers.error} message={t('printers.unavailable')} onRetry={() => void printers.refetch()} />
          ) : (
            <TableSkeleton rows={2} label={t('printers.title')} />
          )}
        </Section>
      ) : (
        // The kit's collection pattern (2026-10-07): a title with how many there are, the Add action, one row per printer.
        <ListCard
          headingLevel="h2"
          title={t('printers.title')}
          summary={list.length === 0 ? t('printers.hint') : t('printers.summary', { count: list.length })}
          count={list.length}
          listLabel={t('printers.listLabel')}
          emptyText={t('printers.empty')}
          action={
            <Button variant="secondary" size="sm" icon={<Icon name="plus" />} onClick={() => setEditor({ mode: 'add' })}>
              {t('printers.add')}
            </Button>
          }
        >
          {list.map((printer) => (
            <PrinterRow
              key={printer.id ?? printer.name}
              organizationId={organizationId}
              venueId={venueId}
              printer={printer}
              printers={list}
              stations={stationRows}
              onEdit={() => setEditor({ mode: 'edit', printer })}
            />
          ))}
        </ListCard>
      )}

      <RecentJobs jobs={jobs.data} error={jobs.isError ? jobs.error : null} onRetry={() => void jobs.refetch()} printers={list} />

      <Dialog
        open={editor !== null}
        onOpenChange={(open) => (open ? undefined : setEditor(null))}
        size="md"
        dirty="auto"
        title={editor?.mode === 'edit' ? t('printers.sheet.editTitle', { name: editor.printer.name }) : t('printers.sheet.addTitle')}
        description={t('printers.sheet.intro')}
      >
        {editor !== null ? (
          <PrinterFlow
            // Remounted per printer, so each starts from its own values and the first step.
            key={editor.mode === 'edit' ? (editor.printer.id ?? 'edit') : 'add'}
            organizationId={organizationId}
            venueId={venueId}
            printer={editor.mode === 'edit' ? editor.printer : null}
            printers={list}
            stations={stationRows}
            stationsLoading={stations.isPending}
            onDone={(saved) => {
              if (saved !== undefined) onReveal(saved);
              setEditor(null);
            }}
          />
        ) : null}
      </Dialog>
    </>
  );
}

/** The polling URL to enter on a cloud printer — shown this once, with a copy button. */
function PollUrl({ revealed, onDismiss }: { readonly revealed: RevealedUrl; readonly onDismiss: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const toast = useToast();
  function copy(): void {
    const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
    if (clipboard === undefined) return;
    void clipboard.writeText(revealed.url).then(
      () => toast.show({ tone: 'ok', title: t('printers.pollUrl.copied') }),
      () => undefined,
    );
  }
  return (
    <div className="console-printers__reveal">
      <Notice
        tone="warn"
        role="status"
        title={t('printers.pollUrl.title', { name: revealed.printerName })}
        action={
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            {t('printers.pollUrl.dismiss')}
          </Button>
        }
      >
        <p>{t('printers.pollUrl.hint')}</p>
        <div className="console-printers__url">
          <code className="console-printers__url-text" aria-label={t('printers.pollUrl.label')}>
            {revealed.url}
          </code>
          <Button variant="secondary" size="sm" onClick={copy}>
            {t('printers.pollUrl.copy')}
          </Button>
        </div>
        <p className="console-printers__once">{t('printers.pollUrl.once')}</p>
      </Notice>
    </div>
  );
}

function PrinterRow({
  organizationId,
  venueId,
  printer,
  printers,
  stations,
  onEdit,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly printer: Printer;
  readonly printers: readonly Printer[];
  readonly stations: readonly Station[];
  readonly onEdit: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useVenueFormatter();
  const test = useTestPrinter(organizationId, venueId);
  const archive = useArchivePrinter(organizationId, venueId);
  const handleJob = usePrintJobHandler();
  const [outcome, setOutcome] = useState<PrintOutcome | null>(null);
  const [confirming, setConfirming] = useState(false);
  const printerId = printer.id;

  const fallback = printer.fallbackPrinterId === null ? undefined : printers.find((other) => other.id === printer.fallbackPrinterId);
  const connection = connectionSummary(printer, stations, t);

  function runTest(): void {
    setOutcome(null);
    test.mutate(printerId, {
      onSuccess: (job) => setOutcome(handleJob(job, job.target, t('printers.testTitle', { name: printer.name }), printer.name)),
      onError: (failure) => setOutcome({ kind: 'failed', message: problem.text(failure, { fallback: t('printing.failed') }) }),
    });
  }

  return (
    <ListRow
      title={printer.name}
      badge={
        <>
          <Badge tone={STATUS_TONE[printer.status]} dot>
            {t(`printers.status.${printer.status}`)}
          </Badge>
          <Badge tone="neutral">{t(`printers.kind.${printer.kind}`)}</Badge>
        </>
      }
      meta={[
        printer.purposes.length === 0 ? t('printers.noPurposes') : printer.purposes.map((purpose) => t(`printers.purpose.${purpose}`)).join(', '),
        t('printers.width', { width: printer.paperWidth }),
        connection,
        ...(printer.cashDrawer ? [t('printers.drawerOn', { pin: printer.drawerPin })] : []),
        ...(fallback === undefined ? [] : [t('printers.fallbackTo', { name: fallback.name })]),
      ]
        .filter((part) => part !== '')
        .join(' · ')}
      actions={
      <div className="console-printers__actions" role="group" aria-label={t('printers.actionsFor', { name: printer.name })}>
        {confirming ? (
          <>
            <span className="console-printers__confirm">{t('printers.archiveConfirm', { name: printer.name })}</span>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
              {t('printers.keep')}
            </Button>
            <Button variant="danger" size="sm" busy={archive.isPending} onClick={() => archive.mutate(printerId, { onSettled: () => setConfirming(false) })}>
              {t('printers.archive')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" size="sm" icon={<Icon name="print" />} busy={test.isPending} onClick={runTest} aria-label={t('printers.testFor', { name: printer.name })}>
              {t('printers.test')}
            </Button>
            <Button variant="ghost" size="sm" icon={<Icon name="pencil" />} onClick={onEdit} aria-label={t('printers.editFor', { name: printer.name })}>
              {t('printers.edit')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(true)} aria-label={t('printers.archiveFor', { name: printer.name })}>
              {t('printers.archive')}
            </Button>
          </>
        )}
      </div>
      }
    >
      {printer.lastError !== null ? <p className="console-printers__error">{t('printers.lastError', { error: printer.lastError })}</p> : null}
      {printer.lastSeenAt !== null ? <p className="console-printers__seen">{t('printers.lastSeen', { time: format.relative(printer.lastSeenAt) })}</p> : null}
      <PrintOutcomeNote outcome={outcome} />
    </ListRow>
  );
}

/** How a printer is reached, in words: the Counter PC and its address, the cloud protocol, or the browser. */
function connectionSummary(printer: Printer, stations: readonly Station[], t: ReturnType<typeof useTranslation>['t']): string {
  if (printer.kind === 'desk' && printer.desk !== null) {
    const station = stations.find((one) => one.id === printer.desk?.stationId);
    const pc = station === undefined ? t('printers.counterPc') : stationName(station, t);
    const where = printer.desk.transport === 'tcp' ? `${printer.desk.host ?? ''}:${printer.desk.port ?? DEFAULT_PORT}` : (printer.desk.queueName ?? '');
    return t('printers.deskVia', { pc, where });
  }
  if (printer.kind === 'cloud' && printer.cloud !== null) return t(`printers.protocol.${printer.cloud.protocol}`);
  return '';
}

function RecentJobs({
  jobs,
  error,
  onRetry,
  printers,
}: {
  readonly jobs: readonly PrintJob[] | undefined;
  readonly error: unknown;
  readonly onRetry: () => void;
  readonly printers: readonly Printer[];
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const printerOf = (job: PrintJob): string =>
    job.printerId === null ? t(`printers.target.${job.target}`) : (printers.find((printer) => printer.id === job.printerId)?.name ?? t('printers.jobs.archived'));
  return (
    <Section title={t('printers.jobs.title')} description={t('printers.jobs.hint')} body="rows">
      {jobs === undefined ? (
        error !== null ? (
          <LoadFailure error={error} message={t('printers.jobs.unavailable')} onRetry={onRetry} />
        ) : (
          <TableSkeleton rows={3} label={t('printers.jobs.title')} />
        )
      ) : jobs.length === 0 ? (
        <p className="console-printers__empty">{t('printers.jobs.empty')}</p>
      ) : (
        // A list rather than a table: it reflows to a phone's width with nothing to scroll sideways.
        <ul className="console-printers__jobs" aria-label={t('printers.jobs.caption')}>
          {jobs.map((job) => (
            <li key={job.id} className="console-printers__job">
              <div className="console-printers__job-main">
                <span className="console-printers__job-what">{t(`printers.content.${job.contentType}`)}</span>
                <span className="console-printers__job-where">{t('printers.jobs.where', { printer: printerOf(job), target: t(`printers.target.${job.target}`) })}</span>
                {job.error !== null ? <span className="console-printers__error">{job.error}</span> : null}
              </div>
              <div className="console-printers__job-side">
                <Badge tone={JOB_TONE[job.status]} dot>
                  {t(`printers.jobStatus.${job.status}`)}
                </Badge>
                <span className="console-num console-subtle">{format.dateTime(job.createdAt, { time: 'short' })}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// -------------------------------------------------------------------------------------------------
// Add or edit: Type → Connection → Routing
// -------------------------------------------------------------------------------------------------

interface Draft {
  readonly kind: PrinterKind;
  readonly name: string;
  readonly purposes: readonly PrinterPurpose[];
  readonly paperWidth: 58 | 80;
  readonly stationId: string;
  readonly transport: 'tcp' | 'spooler';
  readonly host: string;
  readonly port: string;
  readonly queueName: string;
  readonly cloudProtocol: (typeof PROTOCOLS)[number];
  readonly cashDrawer: boolean;
  readonly drawerPin: 2 | 5;
  readonly fallbackPrinterId: string;
}

function draftOf(printer: Printer | null, counterStations: readonly Station[]): Draft {
  return {
    kind: printer?.kind ?? 'desk',
    name: printer?.name ?? '',
    purposes: printer?.purposes ?? ['receipt'],
    paperWidth: printer?.paperWidth === 58 ? 58 : 80,
    stationId: printer?.desk?.stationId ?? counterStations[0]?.id ?? '',
    transport: printer?.desk?.transport ?? 'tcp',
    host: printer?.desk?.host ?? '',
    port: String(printer?.desk?.port ?? DEFAULT_PORT),
    queueName: printer?.desk?.queueName ?? '',
    cloudProtocol: printer?.cloud?.protocol ?? 'star_cloudprnt',
    cashDrawer: printer?.cashDrawer ?? false,
    drawerPin: printer?.drawerPin === 5 ? 5 : 2,
    fallbackPrinterId: printer?.fallbackPrinterId ?? '',
  };
}

type DraftErrors = Partial<Record<'name' | 'stationId' | 'host' | 'port' | 'queueName' | 'form', string>>;

function PrinterFlow({
  organizationId,
  venueId,
  printer,
  printers,
  stations,
  stationsLoading,
  onDone,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly printer: Printer | null;
  readonly printers: readonly Printer[];
  readonly stations: readonly Station[];
  readonly stationsLoading: boolean;
  /** Closes the dialog; `saved` carries a printer whose polling URL should be shown once. */
  readonly onDone: (saved?: Printer) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const toast = useToast();
  const counterStations = useMemo(() => stations.filter((station) => station.role === 'counter'), [stations]);
  const [draft, setDraft] = useState<Draft>(() => draftOf(printer, counterStations));
  const [errors, setErrors] = useState<DraftErrors>({});
  const editing = printer !== null;
  // Editing starts at Connection: a printer's kind is fixed once it is added.
  const [step, setStep] = useState(editing ? 1 : 0);
  const create = useCreatePrinter(organizationId, venueId);
  const update = useUpdatePrinter(organizationId, venueId);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((current) => ({ ...current, [key]: value }));
  // The station list may arrive after the dialog opened: default to the first Counter PC once it does.
  const stationId = draft.stationId !== '' ? draft.stationId : (counterStations[0]?.id ?? '');

  function checkConnection(): boolean {
    const next: DraftErrors = {};
    if (draft.kind === 'desk') {
      if (stationId === '') next.stationId = t('printers.form.stationRequired');
      if (draft.transport === 'tcp') {
        if (draft.host.trim() === '') next.host = t('printers.form.hostRequired');
        const port = Number(draft.port);
        if (draft.port.trim() !== '' && (!Number.isInteger(port) || port < 1 || port > 65535)) next.port = t('printers.form.badPort');
      } else if (draft.queueName.trim() === '') {
        next.queueName = t('printers.form.queueRequired');
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function deskInput(): NonNullable<CreatePrinterRequest['desk']> {
    return draft.transport === 'tcp'
      ? { stationId, transport: 'tcp', host: draft.host.trim(), port: draft.port.trim() === '' ? DEFAULT_PORT : Number(draft.port) }
      : { stationId, transport: 'spooler', queueName: draft.queueName.trim() };
  }

  function refusal(error: unknown): string {
    if (problemCode(error) === PROBLEM.conflict) return t('printers.form.conflict');
    return problem.text(error, { fallback: t('printers.form.failed') });
  }

  function save(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (draft.name.trim() === '') {
      setErrors({ name: t('printers.form.nameRequired') });
      return;
    }
    setErrors({});
    const fallbackPrinterId = draft.fallbackPrinterId === '' ? null : draft.fallbackPrinterId;
    const desk = draft.kind === 'desk';
    if (printer === null) {
      const body: CreatePrinterRequest = {
        name: draft.name.trim(),
        kind: draft.kind,
        purposes: [...draft.purposes],
        paperWidth: draft.paperWidth,
        fallbackPrinterId,
        ...(desk ? { desk: deskInput(), cashDrawer: draft.cashDrawer, drawerPin: draft.drawerPin } : {}),
        ...(draft.kind === 'cloud' ? { cloudProtocol: draft.cloudProtocol } : {}),
      };
      create.mutate(body, {
        onSuccess: (saved) => {
          toast.show({ tone: 'ok', title: t('printers.form.added', { name: saved.name }) });
          onDone(saved);
        },
        onError: (error) => setErrors({ form: refusal(error) }),
      });
      return;
    }
    const body: UpdatePrinterRequest = {
      name: draft.name.trim(),
      purposes: [...draft.purposes],
      paperWidth: draft.paperWidth,
      fallbackPrinterId,
      ...(desk ? { desk: deskInput(), cashDrawer: draft.cashDrawer, drawerPin: draft.drawerPin } : {}),
    };
    update.mutate(
      { printerId: printer.id, body },
      {
        onSuccess: (saved) => {
          toast.show({ tone: 'ok', title: t('printers.form.saved', { name: saved.name }) });
          onDone();
        },
        onError: (error) => setErrors({ form: refusal(error) }),
      },
    );
  }

  function rotateKey(): void {
    if (printer === null) return;
    update.mutate(
      { printerId: printer.id, body: { rotateCloudKey: true } },
      { onSuccess: (saved) => onDone(saved), onError: (error) => setErrors({ form: refusal(error) }) },
    );
  }

  const steps = [
    { id: 'type', label: t('printers.sheet.stepType'), description: t(`printers.kind.${draft.kind}`) },
    { id: 'connection', label: t('printers.sheet.stepConnection') },
    { id: 'routing', label: t('printers.sheet.stepRouting') },
  ];

  return (
    <div className="console-stepper-flow">
      <Stepper current={step} steps={steps} onStepSelect={(index) => setStep(editing ? Math.max(1, index) : index)} />
      {step === 0 ? (
        <div className="console-form">
          <RadioGroup
            label={t('printers.form.kind')}
            value={draft.kind}
            onValueChange={(value) => set('kind', value as PrinterKind)}
            options={KINDS.map((kind) => ({
              value: kind,
              label: (
                <span className="console-printers__option">
                  <span className="console-printers__option-name">{t(`printers.kind.${kind}`)}</span>
                  <span className="console-printers__option-hint">{t(`printers.kindHint.${kind}`)}</span>
                </span>
              ),
            }))}
          />
          <div className="console-form__actions console-form__actions--end">
            <Button variant="ghost" onClick={() => onDone()}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={() => setStep(1)}>
              {t('common.next')}
            </Button>
          </div>
        </div>
      ) : step === 1 ? (
        <form
          className="console-form"
          aria-label={t('printers.sheet.stepConnection')}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (checkConnection()) setStep(2);
          }}
        >
          {draft.kind === 'desk' ? (
            <DeskConnection draft={draft} stationId={stationId} counterStations={counterStations} loading={stationsLoading} errors={errors} set={set} />
          ) : draft.kind === 'cloud' ? (
            <>
              <RadioGroup
                label={t('printers.form.protocol')}
                value={draft.cloudProtocol}
                onValueChange={(value) => set('cloudProtocol', value as Draft['cloudProtocol'])}
                disabled={editing}
                options={PROTOCOLS.map((protocol) => ({ value: protocol, label: t(`printers.protocol.${protocol}`) }))}
              />
              <p className="console-subtle">{editing ? t('printers.form.cloudEditHint') : t('printers.form.cloudHint')}</p>
              {editing ? (
                <div className="console-printers__rotate">
                  <Button variant="secondary" size="sm" busy={update.isPending} onClick={rotateKey}>
                    {t('printers.form.rotate')}
                  </Button>
                  <span className="console-subtle">{t('printers.form.rotateHint')}</span>
                </div>
              ) : null}
            </>
          ) : (
            <p className="console-subtle">{t('printers.form.browserHint')}</p>
          )}
          {errors.form !== undefined ? (
            <Notice tone="danger" role="alert">
              {errors.form}
            </Notice>
          ) : null}
          <div className="console-form__actions console-form__actions--end">
            {editing ? (
              <Button variant="ghost" onClick={() => onDone()}>
                {t('common.cancel')}
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => setStep(0)}>
                {t('common.back')}
              </Button>
            )}
            <Button type="submit" variant="primary" disabled={draft.kind === 'desk' && counterStations.length === 0}>
              {t('common.next')}
            </Button>
          </div>
        </form>
      ) : (
        <form className="console-form" aria-label={t('printers.sheet.stepRouting')} noValidate onSubmit={save}>
          <TextField label={t('printers.form.name')} description={t('printers.form.nameHint')} required maxLength={80} value={draft.name} onChange={(event) => set('name', event.target.value)} error={errors.name} />
          <fieldset className="console-printers__purposes">
            <legend className="clav-field__label">{t('printers.form.purposes')}</legend>
            <p className="console-subtle">{t('printers.form.purposesHint')}</p>
            {PURPOSES.map((purpose) => (
              <Checkbox
                key={purpose}
                label={t(`printers.purpose.${purpose}`)}
                checked={draft.purposes.includes(purpose)}
                onCheckedChange={(checked) =>
                  set('purposes', checked === true ? PURPOSES.filter((one) => one === purpose || draft.purposes.includes(one)) : draft.purposes.filter((one) => one !== purpose))
                }
              />
            ))}
          </fieldset>
          <RadioGroup
            label={t('printers.form.paperWidth')}
            orientation="horizontal"
            value={String(draft.paperWidth)}
            onValueChange={(value) => set('paperWidth', value === '58' ? 58 : 80)}
            options={WIDTHS.map((width) => ({ value: String(width), label: t('printers.width', { width }) }))}
          />
          <SelectField
            label={t('printers.form.fallback')}
            description={t('printers.form.fallbackHint')}
            value={draft.fallbackPrinterId}
            onValueChange={(value) => set('fallbackPrinterId', value)}
            options={[
              { value: '', label: t('printers.form.noFallback') },
              ...printers.filter((other) => other.id !== printer?.id).map((other) => ({ value: other.id, label: other.name })),
            ]}
          />
          {draft.kind === 'desk' ? (
            <div className="console-printers__drawer">
              <Switch label={t('printers.form.cashDrawer')} checked={draft.cashDrawer} onCheckedChange={(checked) => set('cashDrawer', checked)} />
              <p className="console-subtle">{t('printers.form.cashDrawerHint')}</p>
              {draft.cashDrawer ? (
                <RadioGroup
                  label={t('printers.form.drawerPin')}
                  orientation="horizontal"
                  value={String(draft.drawerPin)}
                  onValueChange={(value) => set('drawerPin', value === '5' ? 5 : 2)}
                  options={PINS.map((pin) => ({ value: String(pin), label: t('printers.form.pin', { pin }) }))}
                />
              ) : null}
            </div>
          ) : null}
          {errors.form !== undefined ? (
            <Notice tone="danger" role="alert">
              {errors.form}
            </Notice>
          ) : null}
          <div className="console-form__actions console-form__actions--end">
            <Button variant="ghost" onClick={() => setStep(1)}>
              {t('common.back')}
            </Button>
            <Button type="submit" variant="primary" busy={create.isPending || update.isPending}>
              {editing ? t('printers.form.save') : t('printers.form.create')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

/** A desk printer's connection: the Counter PC that drives it, and TCP 9100 or a Windows printer name. */
function DeskConnection({
  draft,
  stationId,
  counterStations,
  loading,
  errors,
  set,
}: {
  readonly draft: Draft;
  readonly stationId: string;
  readonly counterStations: readonly Station[];
  readonly loading: boolean;
  readonly errors: DraftErrors;
  readonly set: <K extends keyof Draft>(key: K, value: Draft[K]) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  if (loading) return <Skeleton shape="text" lines={3} />;
  if (counterStations.length === 0) {
    return (
      <Notice
        tone="info"
        role="note"
        title={t('printers.form.noCounterTitle')}
        action={
          <Button variant="secondary" size="sm" asChild>
            <Link to="/v/$venueSlug/stations" params={{ venueSlug: venue.slug }}>
              {t('printers.form.toStations')}
            </Link>
          </Button>
        }
      >
        <p>{t('printers.form.noCounter')}</p>
        {/* The Clavian Counter installer itself (F-STN-17): a download, never a command line. */}
        <InstallerDownloads organizationId={venue.organizationId} venueId={venue.id} only="counter" />
      </Notice>
    );
  }
  return (
    <>
      <RadioGroup
        label={t('printers.form.station')}
        description={t('printers.form.stationHint')}
        value={stationId}
        onValueChange={(value) => set('stationId', value)}
        error={errors.stationId}
        options={counterStations.map((station) => ({ value: station.id, label: stationName(station, t) }))}
      />
      <RadioGroup
        label={t('printers.form.transport')}
        orientation="horizontal"
        value={draft.transport}
        onValueChange={(value) => set('transport', value === 'spooler' ? 'spooler' : 'tcp')}
        options={[
          { value: 'tcp', label: t('printers.transport.tcp') },
          { value: 'spooler', label: t('printers.transport.spooler') },
        ]}
      />
      {draft.transport === 'tcp' ? (
        <div className="console-printers__address">
          <TextField label={t('printers.form.host')} description={t('printers.form.hostHint')} required value={draft.host} onChange={(event) => set('host', event.target.value)} error={errors.host} />
          <TextField label={t('printers.form.port')} inputMode="numeric" value={draft.port} onChange={(event) => set('port', event.target.value)} error={errors.port} />
        </div>
      ) : (
        <TextField label={t('printers.form.queueName')} description={t('printers.form.queueHint')} required value={draft.queueName} onChange={(event) => set('queueName', event.target.value)} error={errors.queueName} />
      )}
    </>
  );
}
